import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Users,
  Plus,
  Search,
  Mail,
  Phone,
  MapPin,
  MoreVertical,
  UserPlus,
  Edit,
  Trash2,
  GraduationCap } from
'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger } from
'@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { motion } from 'motion/react';

interface Teacher {
  id: string;
  name: string;
  email: string;
  phone: string;
  school: string;
  subjects: string[];
  status: 'active' | 'inactive' | 'pending';
  joinDate: string;
  avatar?: string;
  classes: number;
  students: number;
}

const TeacherManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive' | 'pending'>('all');
  const { toast } = useToast();

  const mockTeachers: Teacher[] = [
  {
    id: '1',
    name: 'John Doe',
    email: 'john.doe@school.edu',
    phone: '+1 (555) 123-4567',
    school: 'Lincoln High School',
    subjects: ['Mathematics', 'Physics'],
    status: 'active',
    joinDate: '2023-01-15',
    classes: 6,
    students: 184
  },
  {
    id: '2',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@school.edu',
    phone: '+1 (555) 234-5678',
    school: 'Washington Elementary',
    subjects: ['English', 'Literature'],
    status: 'active',
    joinDate: '2023-03-22',
    classes: 4,
    students: 98
  },
  {
    id: '3',
    name: 'Michael Chen',
    email: 'michael.chen@school.edu',
    phone: '+1 (555) 345-6789',
    school: 'Roosevelt Middle School',
    subjects: ['Science', 'Chemistry'],
    status: 'pending',
    joinDate: '2024-01-08',
    classes: 0,
    students: 0
  },
  {
    id: '4',
    name: 'Emily Davis',
    email: 'emily.davis@school.edu',
    phone: '+1 (555) 456-7890',
    school: 'Jefferson High School',
    subjects: ['History', 'Geography'],
    status: 'inactive',
    joinDate: '2022-09-10',
    classes: 2,
    students: 45
  }];


  const filteredTeachers = mockTeachers.filter((teacher) => {
    const matchesSearch = teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.school.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || teacher.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getInitials = (name: string) => {
    return name.split(' ').map((n) => n[0]).join('').toUpperCase();
  };

  const getStatusBadge = (status: Teacher['status']) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800" data-id="6a2uzpnd2" data-path="src/components/admin/TeacherManagement.tsx">Active</Badge>;
      case 'inactive':
        return <Badge variant="secondary" data-id="ylpxj2z2v" data-path="src/components/admin/TeacherManagement.tsx">Inactive</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800" data-id="q3tsg76hm" data-path="src/components/admin/TeacherManagement.tsx">Pending</Badge>;
      default:
        return <Badge variant="outline" data-id="w1aty3qrw" data-path="src/components/admin/TeacherManagement.tsx">Unknown</Badge>;
    }
  };

  const handleAddTeacher = () => {
    toast({
      title: 'Add Teacher',
      description: 'Teacher addition form would open here.'
    });
  };

  const handleEditTeacher = (teacher: Teacher) => {
    toast({
      title: 'Edit Teacher',
      description: `Editing ${teacher.name}'s profile.`
    });
  };

  const handleDeleteTeacher = (teacher: Teacher) => {
    toast({
      title: 'Teacher Deleted',
      description: `${teacher.name} has been removed from the system.`,
      variant: 'destructive'
    });
  };

  const handleApproveTeacher = (teacher: Teacher) => {
    toast({
      title: 'Teacher Approved',
      description: `${teacher.name} has been approved and activated.`
    });
  };

  const stats = {
    total: mockTeachers.length,
    active: mockTeachers.filter((t) => t.status === 'active').length,
    pending: mockTeachers.filter((t) => t.status === 'pending').length,
    inactive: mockTeachers.filter((t) => t.status === 'inactive').length
  };

  return (
    <div className="space-y-6" data-id="tx6v9sbp9" data-path="src/components/admin/TeacherManagement.tsx">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between" data-id="19dmdphfz" data-path="src/components/admin/TeacherManagement.tsx">

        <div data-id="fp7yqndfw" data-path="src/components/admin/TeacherManagement.tsx">
          <h1 className="text-2xl font-bold text-gray-900" data-id="954gpwxmv" data-path="src/components/admin/TeacherManagement.tsx">Teacher Management</h1>
          <p className="text-gray-600" data-id="jonrzxvfu" data-path="src/components/admin/TeacherManagement.tsx">Manage teacher accounts and permissions</p>
        </div>
        <Button onClick={handleAddTeacher} className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700" data-id="jrjm51xa4" data-path="src/components/admin/TeacherManagement.tsx">
          <UserPlus className="h-4 w-4 mr-2" data-id="c9e99hxhy" data-path="src/components/admin/TeacherManagement.tsx" />
          Add Teacher
        </Button>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="c3ygymy1o" data-path="src/components/admin/TeacherManagement.tsx">
        {[
        { label: 'Total Teachers', value: stats.total, color: 'from-blue-400 to-blue-600', icon: Users },
        { label: 'Active', value: stats.active, color: 'from-green-400 to-green-600', icon: Users },
        { label: 'Pending Approval', value: stats.pending, color: 'from-yellow-400 to-yellow-600', icon: Users },
        { label: 'Inactive', value: stats.inactive, color: 'from-gray-400 to-gray-600', icon: Users }].
        map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }} data-id="ttiaqsal0" data-path="src/components/admin/TeacherManagement.tsx">

              <Card data-id="1eqggt8f5" data-path="src/components/admin/TeacherManagement.tsx">
                <CardContent className="p-6" data-id="eaj4uo4hx" data-path="src/components/admin/TeacherManagement.tsx">
                  <div className="flex items-center justify-between" data-id="bdtkp3mcc" data-path="src/components/admin/TeacherManagement.tsx">
                    <div data-id="srrgzzcv5" data-path="src/components/admin/TeacherManagement.tsx">
                      <p className="text-sm font-medium text-gray-600" data-id="7a1mry64s" data-path="src/components/admin/TeacherManagement.tsx">{stat.label}</p>
                      <p className="text-2xl font-bold text-gray-900" data-id="taktjkwe4" data-path="src/components/admin/TeacherManagement.tsx">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-full bg-gradient-to-r ${stat.color}`} data-id="87gpux3gl" data-path="src/components/admin/TeacherManagement.tsx">
                      <Icon className="h-6 w-6 text-white" data-id="8bml6jpa3" data-path="src/components/admin/TeacherManagement.tsx" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>);

        })}
      </div>

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex items-center space-x-4" data-id="aum5hueyy" data-path="src/components/admin/TeacherManagement.tsx">

        <div className="relative flex-1 max-w-md" data-id="e87sc3kxc" data-path="src/components/admin/TeacherManagement.tsx">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" data-id="xmm4cfa5k" data-path="src/components/admin/TeacherManagement.tsx" />
          <Input
            placeholder="Search teachers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10" data-id="poah3y79p" data-path="src/components/admin/TeacherManagement.tsx" />

        </div>
        <Tabs value={filterStatus} onValueChange={(value) => setFilterStatus(value as any)} data-id="vjwhbq6dg" data-path="src/components/admin/TeacherManagement.tsx">
          <TabsList data-id="as4fy9abh" data-path="src/components/admin/TeacherManagement.tsx">
            <TabsTrigger value="all" data-id="gxook6xny" data-path="src/components/admin/TeacherManagement.tsx">All</TabsTrigger>
            <TabsTrigger value="active" data-id="tirmuu8kz" data-path="src/components/admin/TeacherManagement.tsx">Active</TabsTrigger>
            <TabsTrigger value="pending" data-id="pwv9m44z0" data-path="src/components/admin/TeacherManagement.tsx">Pending</TabsTrigger>
            <TabsTrigger value="inactive" data-id="loitavspw" data-path="src/components/admin/TeacherManagement.tsx">Inactive</TabsTrigger>
          </TabsList>
        </Tabs>
      </motion.div>

      {/* Teachers List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }} data-id="kja9kl70m" data-path="src/components/admin/TeacherManagement.tsx">

        <Card data-id="cj9paom6k" data-path="src/components/admin/TeacherManagement.tsx">
          <CardHeader data-id="3f218nht0" data-path="src/components/admin/TeacherManagement.tsx">
            <CardTitle data-id="pf7t6uxxe" data-path="src/components/admin/TeacherManagement.tsx">Teachers ({filteredTeachers.length})</CardTitle>
            <CardDescription data-id="k13ih52lj" data-path="src/components/admin/TeacherManagement.tsx">
              Manage teacher accounts, permissions, and status
            </CardDescription>
          </CardHeader>
          <CardContent data-id="503929e5p" data-path="src/components/admin/TeacherManagement.tsx">
            <div className="space-y-4" data-id="t6dafj5vj" data-path="src/components/admin/TeacherManagement.tsx">
              {filteredTeachers.map((teacher, index) =>
              <motion.div
                key={teacher.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="md2ghcbgl" data-path="src/components/admin/TeacherManagement.tsx">

                  <Avatar className="h-12 w-12" data-id="x71p6jfq7" data-path="src/components/admin/TeacherManagement.tsx">
                    <AvatarImage src={teacher.avatar} alt={teacher.name} data-id="ii7qtable" data-path="src/components/admin/TeacherManagement.tsx" />
                    <AvatarFallback data-id="b4vhzuq5v" data-path="src/components/admin/TeacherManagement.tsx">{getInitials(teacher.name)}</AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4" data-id="ucmu5maly" data-path="src/components/admin/TeacherManagement.tsx">
                    <div data-id="o3l97zlim" data-path="src/components/admin/TeacherManagement.tsx">
                      <h3 className="font-medium text-gray-900" data-id="6pbgt1yyh" data-path="src/components/admin/TeacherManagement.tsx">{teacher.name}</h3>
                      <div className="flex items-center text-sm text-gray-600 mt-1" data-id="rde7fmw8g" data-path="src/components/admin/TeacherManagement.tsx">
                        <Mail className="h-3 w-3 mr-1" data-id="6fvqp5hdr" data-path="src/components/admin/TeacherManagement.tsx" />
                        {teacher.email}
                      </div>
                    </div>
                    
                    <div data-id="88vyc3jr4" data-path="src/components/admin/TeacherManagement.tsx">
                      <div className="flex items-center text-sm text-gray-600" data-id="dw45xb1rl" data-path="src/components/admin/TeacherManagement.tsx">
                        <GraduationCap className="h-3 w-3 mr-1" data-id="spyow1azn" data-path="src/components/admin/TeacherManagement.tsx" />
                        {teacher.school}
                      </div>
                      <div className="flex items-center text-sm text-gray-600 mt-1" data-id="u41hguweu" data-path="src/components/admin/TeacherManagement.tsx">
                        <Phone className="h-3 w-3 mr-1" data-id="55f5vu986" data-path="src/components/admin/TeacherManagement.tsx" />
                        {teacher.phone}
                      </div>
                    </div>
                    
                    <div data-id="0irj7twxe" data-path="src/components/admin/TeacherManagement.tsx">
                      <div className="flex flex-wrap gap-1 mb-1" data-id="lsl6lq82v" data-path="src/components/admin/TeacherManagement.tsx">
                        {teacher.subjects.map((subject) =>
                      <Badge key={subject} variant="secondary" className="text-xs" data-id="vsz8ce43c" data-path="src/components/admin/TeacherManagement.tsx">
                            {subject}
                          </Badge>
                      )}
                      </div>
                      <p className="text-xs text-gray-500" data-id="bvbfuvb2d" data-path="src/components/admin/TeacherManagement.tsx">
                        {teacher.classes} classes • {teacher.students} students
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between" data-id="mosxyov64" data-path="src/components/admin/TeacherManagement.tsx">
                      {getStatusBadge(teacher.status)}
                      <DropdownMenu data-id="dnhkc5i57" data-path="src/components/admin/TeacherManagement.tsx">
                        <DropdownMenuTrigger asChild data-id="cw9fy3mt9" data-path="src/components/admin/TeacherManagement.tsx">
                          <Button variant="ghost" className="h-8 w-8 p-0" data-id="1fg48np0l" data-path="src/components/admin/TeacherManagement.tsx">
                            <MoreVertical className="h-4 w-4" data-id="zbywlvf51" data-path="src/components/admin/TeacherManagement.tsx" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" data-id="d12il6so7" data-path="src/components/admin/TeacherManagement.tsx">
                          <DropdownMenuLabel data-id="0saga2hsq" data-path="src/components/admin/TeacherManagement.tsx">Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleEditTeacher(teacher)} data-id="qm37n7jhn" data-path="src/components/admin/TeacherManagement.tsx">
                            <Edit className="mr-2 h-4 w-4" data-id="v7zlg2u6q" data-path="src/components/admin/TeacherManagement.tsx" />
                            Edit Profile
                          </DropdownMenuItem>
                          {teacher.status === 'pending' &&
                        <DropdownMenuItem onClick={() => handleApproveTeacher(teacher)} data-id="jusa0b34q" data-path="src/components/admin/TeacherManagement.tsx">
                              <UserPlus className="mr-2 h-4 w-4" data-id="krzcaoav6" data-path="src/components/admin/TeacherManagement.tsx" />
                              Approve Teacher
                            </DropdownMenuItem>
                        }
                          <DropdownMenuSeparator data-id="njg3hdouo" data-path="src/components/admin/TeacherManagement.tsx" />
                          <DropdownMenuItem
                          onClick={() => handleDeleteTeacher(teacher)}
                          className="text-red-600" data-id="x7gmlyoxu" data-path="src/components/admin/TeacherManagement.tsx">

                            <Trash2 className="mr-2 h-4 w-4" data-id="z3m7lri0p" data-path="src/components/admin/TeacherManagement.tsx" />
                            Delete Teacher
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </motion.div>
              )}
              
              {filteredTeachers.length === 0 &&
              <div className="text-center py-8 text-gray-500" data-id="3j2a541zt" data-path="src/components/admin/TeacherManagement.tsx">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" data-id="c5lzt7g29" data-path="src/components/admin/TeacherManagement.tsx" />
                  <p data-id="ojzd49qi2" data-path="src/components/admin/TeacherManagement.tsx">No teachers found matching your criteria.</p>
                </div>
              }
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>);

};

export default TeacherManagement;